import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { formatNominalSize } from "@njbsoft/device-input/core";
import { useAppStore } from "@/store";
import {
  Button,
  PageHeader,
  Skeleton,
  Badge,
  VerdictBadge,
  Tabs,
  type TabDef,
} from "@/components/ui";
import { EnforcementIcon, MailIcon } from "@/components/icons";
import { usePermissions } from "@/hooks/use-permissions";
import { useDeviceInstallationDetail } from "@/hooks/use-device-installations";
import { useDeviceRelatedCounts } from "@/hooks/use-device-related-counts";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { type DeviceTab } from "@/components/device-installations/device-tabs";
import { DeviceContextBand } from "@/components/device-installations/device-context-band";
import { DeviceOverviewTab } from "@/components/device-installations/device-overview-tab";
import { DeviceTestingTab } from "@/components/device-installations/device-testing-tab";
import { DeviceComplianceTab } from "@/components/device-installations/device-compliance-tab";
import { DeviceRecordTab } from "@/components/device-installations/device-record-tab";
import { DeviceHistoryTab } from "@/components/device-installations/device-history-tab";
import {
  deviceStatusBadgeVariant,
  isTerminalStatus,
} from "@/components/device-installations/device-installation-status";
import { DevicePendingBanner } from "@/components/change-requests/device-pending-banner";
import { CreateViolationDrawer } from "@/components/violations/create-violation-drawer";
import { RelatedTasksPanel } from "@/components/tasks/related-tasks-panel";
import { EntityDocuments } from "@/components/documents/entity-documents";
import { NotesCard } from "@/components/entity-notes/notes-card";

const ALLOWED_TABS: DeviceTab[] = [
  "overview",
  "testing",
  "compliance",
  "record",
  "history",
  "documents",
  "tasks",
];

interface DeviceDetailSearch {
  tab?: DeviceTab;
}

function parseTab(value: unknown): DeviceTab | undefined {
  if (typeof value !== "string") return undefined;
  return ALLOWED_TABS.find((tab) => tab === value);
}

export const Route = createFileRoute("/_authenticated/device-installations/$id/")({
  component: DeviceInstallationDetailPage,
  validateSearch: (search: Record<string, unknown>): DeviceDetailSearch => ({
    tab: parseTab(search.tab),
  }),
});

function DeviceInstallationDetailPage() {
  const { t } = useTranslation(["installations", "cccp", "notices", "violations", "vocabulary"]);
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();

  const { data: installation, isLoading, isError } = useDeviceInstallationDetail(id);
  useRecentPageTitle(installation?.serial_number ?? undefined);
  const { data: counts } = useDeviceRelatedCounts(id);
  const [violationDrawerOpen, setViolationDrawerOpen] = useState(false);

  const activeTab: DeviceTab = tab ?? "overview";

  const setActiveTab = (next: DeviceTab) => {
    void navigate({
      to: "/device-installations/$id",
      params: { id },
      search: next === "overview" ? {} : { tab: next },
      replace: true,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isError || !installation) {
    return (
      <div className="p-6">
        <div className="rounded-md bg-danger/10 p-4 text-danger">{t("errors.loadListFailed")}</div>
      </div>
    );
  }

  // Header meta line: category · size · listing — bare ids never surface
  // (each part is skipped when its value is absent).
  const make = installation.manufacturer_label || installation.manufacturer_freetext || "";
  const model = installation.model_label || installation.model_freetext || "";
  const makeModel = [make, model].filter(Boolean).join(" · ");
  const metaParts = [
    t(`vocabulary:device_category.${installation.device_category}`),
    installation.size_inches === null || installation.size_inches === undefined
      ? null
      : formatNominalSize(installation.size_inches),
    installation.listing_source
      ? t(`cccp:backflowDevice.listingSource.${installation.listing_source}`)
      : null,
  ].filter(Boolean) as string[];

  // A case is opened about a live assembly; a replaced, removed or abandoned
  // one is a historical record (the device-editability contract's terminal set).
  const canOpenCase = can("violations", "create") && !isTerminalStatus(installation.device_status);
  const canWriteLetter = can("notices", "create");

  // Compliance tab count = open violations + notices (CHIP-AGREEMENT); undefined
  // while the counts query is in flight so no "0" pill flashes.
  const complianceCount = counts
    ? (counts.open_violations_count ?? 0) + (counts.notices_count ?? 0)
    : undefined;

  const tabs: TabDef<DeviceTab>[] = [
    { value: "overview", label: t("detail.tabs.overview") },
    { value: "testing", label: t("detail.tabs.testing"), count: counts?.open_assignments_count },
    { value: "compliance", label: t("detail.tabs.compliance"), count: complianceCount },
    { value: "record", label: t("detail.tabs.record") },
    { value: "history", label: t("detail.tabs.history") },
    { value: "documents", label: t("detail.tabs.documents"), count: counts?.documents_count },
    // No count on Tasks: the RelatedTasksPanel renders tasks of every status, so
    // an "open tasks" pill would disagree with the tab's contents (facility
    // precedent — its Tasks tab carries no count either).
    { value: "tasks", label: t("detail.tabs.tasks") },
  ];

  return (
    <div className="space-y-4 p-6">
      <div className="space-y-4">
        <PageHeader
          title={
            installation.serial_number ? (
              <span className="font-mono">{installation.serial_number}</span>
            ) : (
              t("title")
            )
          }
          meta={
            <div className="space-y-1">
              {makeModel && <div className="text-sm text-on-surface">{makeModel}</div>}
              {metaParts.length > 0 && (
                <div className="text-sm text-muted">{metaParts.join(" · ")}</div>
              )}
            </div>
          }
          actions={
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                {installation.verdict ? (
                  <VerdictBadge
                    verdict={installation.verdict}
                    deviceCategory={installation.device_category}
                  />
                ) : null}
                <Badge variant={deviceStatusBadgeVariant(installation.device_status)}>
                  {t(`vocabulary:device_status.${installation.device_status}`)}
                </Badge>
                {/* A request is waiting on this device — never a verdict of its own. */}
                {installation.pending_change_request && (
                  <Badge variant="outline">{t("change-requests:device.changePending")}</Badge>
                )}
              </div>
              {(canOpenCase || canWriteLetter) && (
                <div className="flex items-center gap-2">
                  {canOpenCase && (
                    <Button variant="secondary" onClick={() => setViolationDrawerOpen(true)}>
                      <EnforcementIcon className="size-4" />
                      {t("violations:create.action")}
                    </Button>
                  )}
                  {canWriteLetter && (
                    <Button
                      variant="secondary"
                      onClick={() =>
                        void navigate({
                          to: "/notices/new",
                          search: { device_installation_id: installation.id },
                        })
                      }
                    >
                      <MailIcon className="size-4" />
                      {t("notices:letters.generate")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          }
        />

        <DevicePendingBanner installation={installation} />

        <DeviceContextBand installation={installation} />

        <div>
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            ariaLabel={t("title")}
          />

          <div
            className="mt-6"
            role="tabpanel"
            id={`tabpanel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
          >
            {activeTab === "overview" && (
              <DeviceOverviewTab installation={installation} onSelectTab={setActiveTab} />
            )}
            {activeTab === "testing" && <DeviceTestingTab installation={installation} />}
            {activeTab === "compliance" && <DeviceComplianceTab installation={installation} />}
            {activeTab === "record" && (
              // The Internal Notes card sits below the record cards (the same
              // band the shared custom-fields card occupies at the tab's foot).
              <div className="space-y-4">
                <DeviceRecordTab installation={installation} />
                <NotesCard parent="device_installation" parentId={id} />
              </div>
            )}
            {activeTab === "history" && <DeviceHistoryTab installation={installation} />}
            {activeTab === "documents" && orgId && (
              <EntityDocuments entityType="device_installation" entityId={id} orgId={orgId} />
            )}
            {activeTab === "tasks" && (
              <RelatedTasksPanel entityType="device_installation" entityId={id} />
            )}
          </div>
        </div>
      </div>

      {violationDrawerOpen && (
        <CreateViolationDrawer
          open
          lockedFacilityId={installation.facility_id}
          lockedInstallationId={installation.id}
          onClose={() => setViolationDrawerOpen(false)}
        />
      )}
    </div>
  );
}
